import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { api, exportUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah } from "@/lib/format";
import { Card, Loading, PageHeader, Button, EmptyState } from "@/components/common";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shiftDay = (s, n) => { const d = new Date(s + "T00:00:00"); d.setDate(d.getDate() + n); return iso(d); };
const fmtLong = (s) => { const d = new Date(s + "T00:00:00"); return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`; };

export default function PaymentReport() {
  const { branch } = useAuth();
  const [sp] = useSearchParams();
  const [scope, setScope] = useState("branch");
  const [mode, setMode] = useState("day");
  const [day, setDay] = useState(iso(new Date()));
  const [ym, setYm] = useState({ y: new Date().getFullYear(), m: new Date().getMonth() });
  const [data, setData] = useState(null);
  const [showZero, setShowZero] = useState(false);

  const params = useMemo(() => {
    const p = { branch_id: scope === "branch" ? branch?.id : undefined, group: "day" };
    if (mode === "day") { p.date_from = day; p.date_to = day; }
    else { p.date_from = iso(new Date(ym.y, ym.m, 1)); p.date_to = iso(new Date(ym.y, ym.m + 1, 0)); }
    return p;
  }, [scope, branch?.id, mode, day, ym]);

  useEffect(() => { setData(null); api.get("/reports/payment-methods", params).then(setData).catch(() => setData({ methods: [], periods: [], total: 0, count: 0 })); }, [params]);

  const methods = (data?.methods || []).filter((m) => showZero || m.amount > 0);
  const label = mode === "day" ? fmtLong(day) : `${MONTHS[ym.m]} ${ym.y}`;
  const prev = () => mode === "day" ? setDay((d) => shiftDay(d, -1)) : setYm(({ y, m }) => m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 });
  const next = () => mode === "day" ? setDay((d) => shiftDay(d, 1)) : setYm(({ y, m }) => m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 });

  return (
    <>
      <PageHeader title="Omset per Metode Bayar" subtitle="Harian & bulanan · tanggal pembayaran diterima" icon={CreditCard}
        actions={<Button variant="outline" onClick={() => window.open(exportUrl("payment_methods", params), "_blank")} data-testid="pr-download"><Download size={16} /> Excel</Button>} />

      <div className="flex flex-wrap gap-2 mb-3">
        {[["branch", branch?.name || "Cabang ini"], ["all", "Semua Cabang"]].map(([k, l]) => <button key={k} onClick={() => setScope(k)} className={`px-3 h-9 rounded-full text-sm font-medium ${scope === k ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
        {[["day", "Harian"], ["month", "Bulanan"]].map(([k, l]) => <button key={k} onClick={() => setMode(k)} className={`px-3 h-9 rounded-full text-sm font-medium ${mode === k ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
      </div>
      <Card className="p-3 mb-4">
        <div className="flex items-center justify-between">
          <button onClick={prev} data-testid="pr-prev" className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><ChevronLeft size={18} /></button>
          <span className="font-semibold text-slate-800" data-testid="pr-period-label">{label}</span>
          <button onClick={next} data-testid="pr-next" className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><ChevronRight size={18} /></button>
        </div>
        <div className="flex flex-wrap gap-2 mt-2 justify-center">
          {mode === "day" ? <button onClick={() => setDay(iso(new Date()))} data-testid="pr-today" className="px-3 h-8 rounded-full text-xs bg-slate-100 text-slate-600">Hari ini</button>
            : <button onClick={() => setYm({ y: new Date().getFullYear(), m: new Date().getMonth() })} className="px-3 h-8 rounded-full text-xs bg-slate-100 text-slate-600">Bulan ini</button>}
          <button onClick={() => setShowZero((v) => !v)} data-testid="pr-toggle-zero" className="px-3 h-8 rounded-full text-xs bg-slate-100 text-slate-600">{showZero ? "Sembunyikan Rp 0" : "Tampilkan semua metode"}</button>
        </div>
      </Card>

      {!data ? <Loading /> : (
        <>
          <Card className="p-4 mb-4 grid grid-cols-2 gap-4" data-testid="pr-totals">
            <div><div className="text-xs text-slate-400">Total omset diterima</div><div className="text-xl font-bold text-slate-800">{rupiah(data.total)}</div></div>
            <div><div className="text-xs text-slate-400">Jumlah pembayaran</div><div className="text-xl font-bold text-slate-800">{data.count}</div></div>
          </Card>
          <Card className="overflow-hidden" data-testid="pr-table">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 bg-slate-50/60 border-b border-slate-100">
                <th className="py-3 px-4 font-medium">Metode Pembayaran</th><th className="py-3 px-4 font-medium text-right">Trx</th><th className="py-3 px-4 font-medium text-right">Nominal</th><th className="py-3 px-4 font-medium text-right">%</th>
              </tr></thead>
              <tbody>
                {methods.map((m) => (
                  <tr key={m.code} className="border-b border-slate-50"><td className="py-3 px-4 font-medium text-slate-800" data-testid={`pr-method-${m.code}`}>{m.name}</td><td className="py-3 px-4 text-right text-slate-500">{m.count}</td><td className="py-3 px-4 text-right font-semibold">{rupiah(m.amount)}</td><td className="py-3 px-4 text-right text-slate-400">{m.share_pct}%</td></tr>
                ))}
                <tr className="bg-slate-50 font-bold"><td className="py-3 px-4">TOTAL</td><td className="py-3 px-4 text-right">{data.count}</td><td className="py-3 px-4 text-right">{rupiah(data.total)}</td><td className="py-3 px-4 text-right">100%</td></tr>
              </tbody>
            </table>
            {methods.length === 0 && <EmptyState icon={CreditCard} title="Belum ada pembayaran" message="Belum ada pembayaran pada periode ini." />}
          </Card>
          {mode === "month" && data.periods?.length > 0 && (
            <div className="mt-4 space-y-2">
              <h3 className="font-semibold text-slate-700">Rincian per Tanggal</h3>
              {data.periods.map((p) => (
                <Card key={p.period} className="p-4">
                  <div className="flex justify-between font-medium mb-1"><span className="text-slate-800">{fmtLong(p.period)}</span><span className="font-bold">{rupiah(p.total)}</span></div>
                  {Object.entries(p.by_method).map(([code, amt]) => <div key={code} className="flex justify-between text-sm text-slate-500"><span>{data.method_names?.[code] || code}</span><span>{rupiah(amt)}</span></div>)}
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}
